import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PoPageDynamicSearchComponent, PoPageDynamicSearchFilters, PoPageDynamicSearchLiterals } from "@po-ui/ng-templates";
import {
  PoCheckboxGroupOption,
  PoDialogService,
  PoDisclaimer,
  PoModalComponent,
  PoMultiselectFilterMode,
  PoNotificationService,
  PoPageAction,
  PoTableAction,
  PoTableColumn,
  PoTableColumnSort,
  PoTableComponent,
} from "@po-ui/ng-components";
import { Subject, Subscription, forkJoin, of } from "rxjs";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";

import { Area } from "src/app/interfaces/area";
import { Tribe } from "src/app/interfaces/tribe";
import { MainService } from "src/app/app.service";
import { AreaService } from "../services/area.service";
import { TribeService } from "../services/tribe.service";
import { UserTransferService } from "../services/user-transfer.service";
import { AccessCatalogEntry, AccessCatalogService } from "../services/access-catalog.service";
import { AreaComboFilter } from "../services/area-combo.filter";
import { UserAccessItem, UsersAccessFilters, UsersAccessService, UsersAccessSummary } from "../services/users-access.service";
import { UserEditComponent } from "../user-edit/user-edit.component";
import { asList, disclaimerValue, disclaimerValues, showFilterDisclaimers } from "../search-filters.util";
import { TableHeightFitter, groupCatalog } from "../users-admin.utils";
import { resolveRoleTag, roleTagColor } from "src/app/shared/role-tags";

const PAGE_SIZE = 50;

interface BulkAccessGroup {
  group: string;
  label: string;
  options: Array<PoCheckboxGroupOption>;
  selected: Array<string>;
}

/** Opção "Sem tribe" no campo Tribe e "Sem área" no campo Área (somam com as demais escolhas). */
export const NONE_OPTION = "__none__";

@Component({
  selector: "app-users-access",
  templateUrl: "./users-access.component.html",
  styleUrls: ["./users-access.component.css"],
})
export class UsersAccessComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(PoPageDynamicSearchComponent, { static: true }) search: PoPageDynamicSearchComponent;
  @ViewChild("bulkGrantModal", { static: true }) bulkGrantModal: PoModalComponent;
  @ViewChild("bulkRevokeModal", { static: true }) bulkRevokeModal: PoModalComponent;
  @ViewChild("bulkMoveModal", { static: true }) bulkMoveModal: PoModalComponent;
  @ViewChild("userEdit", { static: true }) userEdit: UserEditComponent;
  @ViewChild(PoTableComponent) table: PoTableComponent;

  loading = false;
  loadingShowMore = false;
  exporting = false;
  items: Array<UserAccessItem> = [];
  total = 0;
  summary: UsersAccessSummary | null = null;

  tribes: Array<Tribe> = [];
  areas: Array<Area> = [];
  accessCatalog: Array<AccessCatalogEntry> = [];

  // Filtros (NONE_OPTION nas listas = "Sem tribe" / "Sem área")
  q = "";
  selectedTribes: Array<string> = [];
  selectedAreas: Array<string> = [];
  selectedRoles: Array<string> = [];

  sortBy = "username";
  sortOrder: "asc" | "desc" = "asc";

  /**
   * Seleção guardada por usuário e mantida entre buscas, filtros e páginas (como um carrinho).
   * Guarda o item para o "Revogar acesso" saber os acessos de quem saiu da lista visível.
   */
  private readonly selectedUsers = new Map<string, UserAccessItem>();

  get selectedUsernames(): Array<string> {
    return [...this.selectedUsers.keys()];
  }

  /** Selecionados que não estão na lista exibida agora. */
  get hiddenSelectedCount(): number {
    const visible = new Set(this.items.map((i) => i.username));
    return this.selectedUsernames.filter((u) => !visible.has(u)).length;
  }

  bulkGrantRoles: Array<string> = [];
  bulkRevokeRoles: Array<string> = [];
  bulkMoveToAreaId = "";
  bulkProcessing = false;

  // Opções (campos, não getters: arrays novos a cada ciclo quebram o change detection)
  readonly areaCombo = new AreaComboFilter();

  /** Campos da busca avançada nativa do PO (po-page-dynamic-search). */
  searchFilters: Array<PoPageDynamicSearchFilters> = [];
  readonly searchLiterals: PoPageDynamicSearchLiterals = {
    searchPlaceholder: "Buscar usuário",
    filterTitle: "Busca avançada",
    filterConfirmLabel: "Aplicar",
    disclaimerGroupTitle: "Filtros ativos",
  };

  /** Acessos dos modais de lote agrupados por produto (ESP, MCP, Smart Review), como no Editar. */
  grantGroups: Array<BulkAccessGroup> = [];
  revokeGroups: Array<BulkAccessGroup> = [];

  /**
   * Ações em lote só aparecem com dois ou mais usuários selecionados (nada de botão cinza
   * desabilitado); para um só, o Editar da linha já faz o mesmo.
   */
  pageActions: Array<PoPageAction> = [];

  private readonly defaultActions: Array<PoPageAction> = [
    { label: "Exportar CSV", icon: "po-icon-download", action: () => this.exportCsv() },
    { label: "Atualizar", icon: "po-icon-refresh", action: () => this.reloadAll() },
  ];

  private readonly selectionActions: Array<PoPageAction> = [
    { label: "Mover para área", icon: "po-icon-arrow-right", action: () => this.openBulkMove() },
    { label: "Conceder acesso", icon: "po-icon-plus", action: () => this.openBulkGrant() },
    { label: "Revogar acesso", icon: "po-icon-minus", action: () => this.openBulkRevoke() },
  ];

  /** A tabela ocupa o que sobra da tela: a página não rola, só as linhas. */
  readonly tableFit: TableHeightFitter;

  readonly columns: Array<PoTableColumn> = [
    { property: "username", label: "Usuário", width: "22%", type: "link", action: (_: string, row: UserAccessItem) => this.userEdit.open(row) },
    { property: "tribeName", label: "Tribe", width: "16%", type: "cellTemplate" },
    { property: "areaName", label: "Área", width: "32%", type: "cellTemplate" },
    { property: "roles", label: "Acessos", type: "cellTemplate", sortable: false },
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: "Editar", icon: "po-icon-edit", action: (row: UserAccessItem) => this.userEdit.open(row) },
    { label: "Histórico", icon: "po-icon-clock", action: (row: UserAccessItem) => this.userEdit.openHistory(row) },
  ];


  private readonly destroy$ = new Subject<void>();
  private readonly loadRequests = new Subject<void>();
  private subscriptions = new Subscription();
  /** "Carregar mais" em andamento: cancelado quando a lista recarrega com outro filtro. */
  private showMoreSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private usersAccessService: UsersAccessService,
    private areaService: AreaService,
    private tribeService: TribeService,
    private userTransferService: UserTransferService,
    private accessCatalogService: AccessCatalogService,
    private mainService: MainService,
    private poDialog: PoDialogService,
    private poNotification: PoNotificationService,
    elementRef: ElementRef<HTMLElement>,
    zone: NgZone,
  ) {
    this.tableFit = new TableHeightFitter(elementRef.nativeElement, zone);
    this.watchLoadRequests();
  }

  ngOnInit(): void {
    this.refreshPageActions();
    // Atalhos vindos da Estrutura: ?area=<id> ou ?tribe=<id>
    this.subscriptions.add(
      this.route.queryParamMap.subscribe((params) => {
        this.selectedAreas = params.getAll("area");
        this.selectedTribes = params.getAll("tribe");
        this.syncDisclaimers();
        this.load();
      }),
    );
    this.loadStructure();
  }

  ngAfterViewInit(): void {
    this.tableFit.start();
  }

  ngOnDestroy(): void {
    this.tableFit.stop();
    this.showMoreSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

  // ── Dados de apoio ──────────────────────────────────────────────────────────

  /** Estrutura, catálogo e contagens "Sem tribe/Sem área": não dependem do filtro, só mudam com Atualizar ou mover usuários. */
  private loadStructure(): void {
    this.subscriptions.add(
      forkJoin({
        tribes: this.tribeService.list(),
        areas: this.areaService.list(),
        catalog: this.accessCatalogService.getCatalog(),
        summary: this.usersAccessService.summary().pipe(catchError(() => of(null))),
      }).subscribe(({ tribes, areas, catalog, summary }) => {
        this.tribes = tribes;
        this.areas = areas;
        this.accessCatalog = catalog;
        this.summary = summary;
        this.refreshOptions();
      }),
    );
  }

  private refreshOptions(): void {

    this.areaCombo.setData(this.areas, this.tribes);

    const count = (n?: number) => (n === undefined ? "" : ` (${n})`);
    this.searchFilters = [
      {
        property: "tribe",
        label: "Tribe",
        optionsMulti: true,
        filterMode: PoMultiselectFilterMode.contains,
        gridColumns: 6,
        options: [
          { value: NONE_OPTION, label: `Sem tribe${count(this.summary?.noTribe)}` },
          ...this.tribes.slice().sort((a, b) => a.tribe.localeCompare(b.tribe)).map((t) => ({ value: t.id, label: t.tribe })),
        ],
      },
      {
        property: "area",
        label: "Área",
        optionsMulti: true,
        filterMode: PoMultiselectFilterMode.contains,
        gridColumns: 6,
        options: [
          { value: NONE_OPTION, label: `Sem área${count(this.summary?.noArea)}` },
          ...this.areas.slice().sort((a, b) => a.area.localeCompare(b.area)).map((a) => ({ value: a.id, label: a.area })),
        ],
      },
      {
        property: "role",
        label: "Acesso",
        optionsMulti: true,
        filterMode: PoMultiselectFilterMode.contains,
        gridColumns: 12,
        options: this.accessCatalog.map((r) => ({ value: r.key, label: r.label })),
      },
    ];
    this.syncDisclaimers();
  }

  // ── Filtros (po-page-dynamic-search) ────────────────────────────────────────

  onQuickSearch(q: string): void {
    this.q = (q ?? "").trim();
    this.load();
  }

  onAdvancedSearch(filter: Record<string, unknown> | null): void {
    if (this.syncing) {
      return;
    }
    // A busca avançada do PO troca os disclaimers e limpa o campo de busca rápida
    this.q = "";
    this.selectedTribes = asList(filter?.["tribe"]);
    this.selectedAreas = asList(filter?.["area"]);
    this.selectedRoles = asList(filter?.["role"]);
    this.load();
  }

  onChangeDisclaimers(disclaimers: Array<PoDisclaimer>): void {
    this.q = disclaimerValue(disclaimers, "search") ?? "";
    this.selectedTribes = disclaimerValues(disclaimers, "tribe");
    this.selectedAreas = disclaimerValues(disclaimers, "area");
    this.selectedRoles = disclaimerValues(disclaimers, "role");
    this.load();
  }

  private syncing = false;

  /** Atalhos (?area=, ?tribe=) e opções recarregadas aparecem como disclaimers do PO, sem disparar outra busca. */
  private syncDisclaimers(): void {
    if (!this.search || !this.searchFilters.length) {
      return;
    }
    const filter: Record<string, Array<string>> = {};
    if (this.selectedTribes.length) filter["tribe"] = this.selectedTribes;
    if (this.selectedAreas.length) filter["area"] = this.selectedAreas;
    if (this.selectedRoles.length) filter["role"] = this.selectedRoles;
    if (Object.keys(filter).length) {
      this.syncing = true;
      showFilterDisclaimers(this.search, this.searchFilters, filter, this.q);
      this.syncing = false;
    }
  }

  private get filters(): UsersAccessFilters {
    const ids = (list: Array<string>) => list.filter((v) => v !== NONE_OPTION);
    const tribes = ids(this.selectedTribes);
    const areas = ids(this.selectedAreas);
    return {
      q: this.q || undefined,
      tribe: tribes.length ? tribes : undefined,
      area: areas.length ? areas : undefined,
      role: this.selectedRoles.length ? this.selectedRoles : undefined,
      noTribe: this.selectedTribes.includes(NONE_OPTION) || undefined,
      noArea: this.selectedAreas.includes(NONE_OPTION) || undefined,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    };
  }

  // ── Lista ───────────────────────────────────────────────────────────────────

  private watchLoadRequests(): void {
    this.loadRequests
      .pipe(
        switchMap(() =>
          this.usersAccessService.list({ ...this.filters, offset: 0, limit: PAGE_SIZE }).pipe(
            map((page) => ({ page })),
            catchError(() => of({ page: null })),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ page }) => {
        this.loading = false;
        this.setItems(this.markSelected(page?.items ?? []));
        this.total = page?.total ?? 0;
        this.refreshPageActions();
      });
  }

  load(): void {
    this.cancelShowMore();
    this.loading = true;
    this.loadRequests.next();
  }

  reloadAll(): void {
    this.loadStructure();
    this.load();
  }

  onShowMore(): void {
    if (this.loadingShowMore || this.allLoaded) {
      return;
    }
    this.loadingShowMore = true;
    this.showMoreSub = this.usersAccessService.list({ ...this.filters, offset: this.items.length, limit: PAGE_SIZE }).subscribe({
      next: (page) => {
        this.setItems([...this.items, ...this.markSelected(page.items)]);
        this.total = page.total;
      },
      complete: () => (this.loadingShowMore = false),
      error: () => (this.loadingShowMore = false),
    });
  }

  private cancelShowMore(): void {
    this.showMoreSub?.unsubscribe();
    this.showMoreSub = undefined;
    this.loadingShowMore = false;
  }

  get allLoaded(): boolean {
    return this.items.length >= this.total;
  }

  onSortBy(sort: PoTableColumnSort): void {
    const property = sort?.column?.property;
    if (!property) {
      return;
    }
    const sortable: Record<string, string> = { username: "username", tribeName: "tribe", areaName: "area", updatedAt: "updatedAt" };
    this.sortBy = sortable[property] ?? "username";
    this.sortOrder = sort.type === "ascending" ? "asc" : "desc";
    this.load();
  }

  // ── Seleção e lotes ─────────────────────────────────────────────────────────

  private refreshPageActions(): void {
    this.pageActions = this.selectedUsernames.length > 1 ? [...this.selectionActions, ...this.defaultActions] : [...this.defaultActions];
  }

  /** Na lista, a área aparece sem o prefixo da tribe (a tribe já está na coluna ao lado). */
  areaShortName(row: UserAccessItem): string {
    const name = row.areaName || "";
    if (!row.tribeName) return name;
    const prefix = new RegExp(`^(Tribe\\s+)?${row.tribeName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+[-–]\\s+`, "i");
    return name.replace(prefix, "") || name;
  }

  /** Mesma cor do perfil do usuário (shared/role-tags). */
  roleColor(role: string): string {
    return roleTagColor(role);
  }

  /** Branco nas cores escuras, padrão nas claras (igual ao perfil). */
  roleTextColor(role: string): string | undefined {
    return resolveRoleTag(role).textColor;
  }

  /** Mesmo nome do perfil ("Smart Review · Run"); a chave aparece no tooltip. */
  roleLabel(role: string): string {
    return resolveRoleTag(role).label;
  }

  /** Todos os acessos do usuário, como no perfil (inclusive o papel base "user"), sem repetição. */
  userRoles(row: UserAccessItem): Array<string> {
    return Array.from(new Set((row.roles || []).filter((r) => typeof r === "string" && r.trim() !== "")));
  }

  /**
   * O po-table só recalcula o checkbox do cabeçalho quando se clica numa linha; com a lista
   * recarregada ou a seleção limpa por código, ele ficava em "parcial" sem nada marcado.
   */
  private setItems(items: Array<UserAccessItem>): void {
    this.items = items;
    if (!this.table) return;
    const selected = items.filter((i) => i.$selected).length;
    // null = parcial (é o valor que o próprio po-table usa)
    this.table.selectAll = (selected && selected === items.length ? true : selected ? null : false) as boolean;
  }

  /** Marca na lista recarregada quem já estava selecionado (e atualiza os dados guardados dele). */
  private markSelected(items: Array<UserAccessItem>): Array<UserAccessItem> {
    return items.map((i) => {
      const selected = this.selectedUsers.has(i.username);
      if (selected) this.selectedUsers.set(i.username, i);
      return { ...i, $selected: selected } as UserAccessItem;
    });
  }

  onRowSelected(row: UserAccessItem): void {
    this.selectedUsers.set(row.username, row);
    this.refreshPageActions();
  }

  onRowUnselected(row: UserAccessItem): void {
    this.selectedUsers.delete(row.username);
    this.refreshPageActions();
  }

  /** "Marcar todos" vale para a lista visível; quem foi selecionado em outra busca continua. */
  onAllSelected(): void {
    this.items.forEach((i) => this.selectedUsers.set(i.username, i));
    this.refreshPageActions();
  }

  onAllUnselected(): void {
    this.items.forEach((i) => this.selectedUsers.delete(i.username));
    this.refreshPageActions();
  }

  clearSelection(): void {
    this.selectedUsers.clear();
    this.setItems(this.items.map((i) => ({ ...i, $selected: false }) as UserAccessItem));
    this.refreshPageActions();
  }

  openBulkGrant(): void {
    this.bulkGrantRoles = [];
    this.grantGroups = this.buildBulkGroups(this.accessCatalog, this.bulkGrantRoles);
    this.bulkGrantModal.open();
  }

  openBulkRevoke(): void {
    const present = new Set<string>();
    this.selectedUsers.forEach((u) => (u.roles || []).forEach((r) => present.add(r)));
    this.bulkRevokeRoles = [];
    this.revokeGroups = this.buildBulkGroups(this.accessCatalog.filter((r) => present.has(r.key)), this.bulkRevokeRoles);
    this.bulkRevokeModal.open();
  }

  onBulkGrantChange(group: string, selected: Array<string>): void {
    const next = this.mergeGroupSelection(this.bulkGrantRoles, group, selected);
    this.bulkGrantRoles = this.accessCatalogService.applyDependencies(this.accessCatalog, next, this.bulkGrantRoles);
    this.grantGroups = this.buildBulkGroups(this.accessCatalog, this.bulkGrantRoles);
  }

  onBulkRevokeChange(group: string, selected: Array<string>): void {
    this.bulkRevokeRoles = this.mergeGroupSelection(this.bulkRevokeRoles, group, selected);
    this.revokeGroups = this.revokeGroups.map((g) => ({ ...g, selected: this.bulkRevokeRoles.filter((r) => g.options.some((o) => o.value === r)) }));
  }

  trackBulkGroup = (_: number, g: BulkAccessGroup) => g.group;

  /** Troca a seleção de um grupo, mantendo a dos outros grupos. */
  private mergeGroupSelection(current: Array<string>, group: string, selected: Array<string>): Array<string> {
    const groupKeys = this.accessCatalog.filter((e) => e.group === group).map((e) => e.key);
    return [...current.filter((r) => !groupKeys.includes(r)), ...(selected ?? [])];
  }

  private buildBulkGroups(entries: Array<AccessCatalogEntry>, selected: Array<string>): Array<BulkAccessGroup> {
    return groupCatalog(entries).map((g) => ({
      group: g.group,
      label: g.label,
      options: g.entries.map((e) => ({ value: e.key, label: e.dependsOn ? `${e.label} (requer ${resolveRoleTag(e.dependsOn).label})` : e.label })),
      selected: selected.filter((r) => g.entries.some((e) => e.key === r)),
    }));
  }

  confirmBulkGrant(): void {
    if (!this.bulkGrantRoles.length) {
      return;
    }
    this.poDialog.confirm({
      title: "Conceder acesso",
      message: `Conceder <strong>${this.bulkGrantRoles.join(", ")}</strong> a <strong>${this.selectedUsernames.length}</strong> usuário(s)?`,
      confirm: () => this.doBulkUpdateRoles({ add: this.bulkGrantRoles }, this.bulkGrantModal),
    });
  }

  confirmBulkRevoke(): void {
    if (!this.bulkRevokeRoles.length) {
      return;
    }
    const me = (this.mainService.userToken?.username || "").toLowerCase();
    if (this.bulkRevokeRoles.includes("superuser") && this.selectedUsernames.some((u) => u.toLowerCase() === me)) {
      this.poNotification.warning("Você não pode remover o seu próprio acesso superuser.");
      return;
    }
    const dependents = this.accessCatalog
      .filter((e) => e.dependsOn && this.bulkRevokeRoles.includes(e.dependsOn) && !this.bulkRevokeRoles.includes(e.key))
      .map((e) => e.key);
    const note = dependents.length ? `<br/><br/>Quem depende desses acessos também perde: <strong>${dependents.join(", ")}</strong>.` : "";
    this.poDialog.confirm({
      title: "Revogar acesso",
      message: `Revogar <strong>${this.bulkRevokeRoles.join(", ")}</strong> de <strong>${this.selectedUsernames.length}</strong> usuário(s)?${note}`,
      confirm: () => this.doBulkUpdateRoles({ remove: this.bulkRevokeRoles }, this.bulkRevokeModal),
    });
  }

  private doBulkUpdateRoles(payload: { add?: Array<string>; remove?: Array<string> }, modal: PoModalComponent): void {
    this.bulkProcessing = true;
    this.subscriptions.add(
      this.usersAccessService.bulkUpdateRoles({ usernames: [...this.selectedUsernames], ...payload }).subscribe({
        next: (result) => {
          this.poNotification.success(`Acessos alterados em ${result.updated} usuário(s).`);
          modal.close();
          this.clearSelection();
          this.load();
        },
        complete: () => (this.bulkProcessing = false),
        error: () => (this.bulkProcessing = false),
      }),
    );
  }

  openBulkMove(): void {
    this.bulkMoveToAreaId = "";
    this.bulkMoveModal.open();
  }

  confirmBulkMove(): void {
    if (!this.bulkMoveToAreaId) {
      return;
    }
    const area = this.areas.find((a) => a.id === this.bulkMoveToAreaId);
    this.poDialog.confirm({
      title: "Mover para área",
      message: `Mover <strong>${this.selectedUsernames.length}</strong> usuário(s) para <strong>${area?.area ?? ""}</strong>${area?.tribeName ? ` (tribe ${area.tribeName})` : ""}?`,
      confirm: () => this.doBulkMove(),
    });
  }

  private doBulkMove(): void {
    this.bulkProcessing = true;
    this.subscriptions.add(
      this.userTransferService.transfer(this.bulkMoveToAreaId, [...this.selectedUsernames]).subscribe({
        next: (result) => {
          this.poNotification.success(`${result.moved} usuário(s) movido(s).`);
          this.bulkMoveModal.close();
          this.clearSelection();
          this.reloadAll();
        },
        complete: () => (this.bulkProcessing = false),
        error: () => (this.bulkProcessing = false),
      }),
    );
  }

  // ── Exportação ──────────────────────────────────────────────────────────────

  exportCsv(): void {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    this.subscriptions.add(
      this.usersAccessService.exportCsv(this.filters).subscribe({
        next: (csv) => {
          const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "usuarios-acessos.csv";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        complete: () => (this.exporting = false),
        error: () => (this.exporting = false),
      }),
    );
  }
}
